import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import api from '../services/apiService';
import Project from '../../common/Utils/project';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';

// Custom Date Range Picker Component
const DateRangePicker = ({ fromDate, toDate, onDateChange, onClose }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingStart, setSelectingStart] = useState(true);
  const [hoveredDate, setHoveredDate] = useState(null);

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek };
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1));
  };

  const isDateDisabled = (date) => {
    return date > new Date();
  };

  const isDateInRange = (date) => {
    if (!fromDate || !toDate) return false;
    return date >= fromDate && date <= toDate;
  };

  const isDateHovered = (date) => {
    if (!hoveredDate || !fromDate || toDate) return false;
    const min = Math.min(fromDate.getTime(), hoveredDate.getTime());
    const max = Math.max(fromDate.getTime(), hoveredDate.getTime());
    return date.getTime() >= min && date.getTime() <= max;
  };

  const handleDateClick = (date) => {
    if (isDateDisabled(date)) return;

    if (selectingStart || !fromDate) {
      onDateChange(date, null);
      setSelectingStart(false);
    } else {
      if (date < fromDate) {
        onDateChange(date, fromDate);
      } else {
        const diffDays = (date.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24);
        if (diffDays > 5) {
          return;
        }
        onDateChange(fromDate, date);
        setTimeout(() => onClose(), 300);
      }
      setSelectingStart(true);
    }
  };

  const renderMonth = (monthOffset = 0) => {
    const displayMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + monthOffset, 1);
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(displayMonth);
    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="h-10" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(displayMonth.getFullYear(), displayMonth.getMonth(), day);
      const isDisabled = isDateDisabled(date);
      const isStart = fromDate && date.toDateString() === fromDate.toDateString();
      const isEnd = toDate && date.toDateString() === toDate.toDateString();
      const inRange = isDateInRange(date);
      const inHoverRange = isDateHovered(date);

      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(date)}
          onMouseEnter={() => setHoveredDate(date)}
          disabled={isDisabled}
          style={{
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            fontSize: '14px',
            position: 'relative',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            color: isDisabled ? 'rgba(255, 255, 255, 0.3)' : 'white',
            backgroundColor:
              isStart || isEnd
                ? 'rgba(165, 8, 17, 1)'
                : inRange
                  ? 'rgba(165, 8, 17, 0.15)'
                  : inHoverRange
                    ? 'rgba(165, 8, 17, 0.1)'
                    : 'transparent',
            fontWeight: isStart || isEnd ? '600' : '400',
            transition: 'all 0.2s ease',
            border: 'none',
          }}
          onMouseOver={(e) => {
            if (!isDisabled && !isStart && !isEnd) {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
            }
          }}
          onMouseOut={(e) => {
            if (!isDisabled && !isStart && !isEnd && !inRange && !inHoverRange) {
              e.currentTarget.style.backgroundColor = 'transparent';
            } else if (!isDisabled && !isStart && !isEnd) {
              e.currentTarget.style.backgroundColor = inRange ? 'rgba(165, 8, 17, 0.15)' : 'rgba(165, 8, 17, 0.1)';
            }
          }}
        >
          {day}
        </button>,
      );
    }

    return (
      <div style={{ flex: 1, padding: '0 16px' }}>
        <div style={{ textAlign: 'center', fontWeight: '600', marginBottom: '16px', fontSize: '15px' }}>
          {monthNames[displayMonth.getMonth()]} {displayMonth.getFullYear()}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '8px' }}>
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
            <div
              key={day}
              style={{
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.5)',
                fontWeight: '500',
              }}
            >
              {day}
            </div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>{days}</div>
      </div>
    );
  };

  return (
    <div
      style={{
        backgroundColor: '#1c1c1c',
        borderRadius: '8px',
        padding: '20px',
        boxShadow: '0 0 30px rgba(0, 0, 0, 0.4)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        animation: 'slideUp 0.25s ease-in-out',
      }}
      onMouseLeave={() => setHoveredDate(null)}
    >
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(10px);
            opacity: 0.8;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <button
          onClick={() => navigateMonth(-1)}
          style={{
            padding: '8px',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '6px',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <ChevronLeft size={20} />
        </button>

        <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)', fontWeight: '500' }}>
          {fromDate && toDate
            ? `${fromDate.toLocaleDateString()} - ${toDate.toLocaleDateString()}`
            : fromDate
              ? `${fromDate.toLocaleDateString()} - Select end date`
              : 'Select start date'}
        </div>

        <button
          onClick={() => navigateMonth(1)}
          style={{
            padding: '8px',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '6px',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <ChevronRight size={20} />
        </button>
      </div>

      <div style={{ display: 'flex', gap: '24px' }}>
        {renderMonth(0)}
        {renderMonth(1)}
      </div>

      <div
        style={{
          marginTop: '16px',
          paddingTop: '16px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          textAlign: 'center',
          fontSize: '12px',
          color: 'rgba(255, 255, 255, 0.5)',
        }}
      >
        Maximum 5-day range allowed
      </div>
    </div>
  );
};

// Main Modal Component
export const ShipAgentLogModal = ({ show, onClose, agentId }) => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const { selectedTenantId } = useTenantSelection();
  const { addToast } = useToast();
  if (!show) return null;

  const handleDateChange = (start, end) => {
    setFromDate(start);
    setToDate(end);
  };

  const validateDates = () => {
    if (!fromDate || !toDate) {
      alert('Please select both start and end dates.');
      return false;
    }
    return true;
  };

  const handleShipLogs = async () => {
    if (!validateDates()) return;
    setLoading(true);

    // Set fromDate to start of day (00:00:00) in UTC
    const startOfDay = new Date(Date.UTC(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate(), 0, 0, 0, 0));
    
    // Set toDate to end of day (23:59:59.999) in UTC
    const endOfDay = new Date(Date.UTC(toDate.getFullYear(), toDate.getMonth(), toDate.getDate(), 23, 59, 59, 999));

    const payload = {
      agentIdList: [agentId],
      fromDate: startOfDay.toISOString(),
      toDate: endOfDay.toISOString(),
    };

    try {
      await api.post(`${Project.api}patchcenterservice/RemediationPlan/${selectedTenantId}/ship-agent-log`, payload);
      addToast('Logs shipped successfully!', 'success', 4000, 'Ship Agent Log');
      onClose();
    } catch (error) {
      console.error('Error shipping logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateDisplay = () => {
    if (fromDate && toDate) {
      return `${fromDate.toLocaleDateString()} - ${toDate.toLocaleDateString()}`;
    }
    return 'Select date range';
  };

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0.8;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>

      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          zIndex: 9999,
          animation: 'fadeIn 0.2s ease-in-out',
        }}
        onClick={onClose}
      >
        <div
          style={{
            backgroundColor: '#1c1c1c',
            borderRadius: '8px',
            boxShadow: '0 0 30px rgba(0, 0, 0, 0.4)',
            color: 'white',
            width: '900px',
            maxWidth: '90vw',
            minHeight: showPicker ? '850px' : 'auto',
            maxHeight: '95vh',
            transition: 'all 0.3s ease-in-out',
            display: 'flex',
            flexDirection: 'column',
            animation: 'slideUp 0.25s ease-in-out',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              padding: '20px 24px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Ship Agent Logs</h2>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '24px',
                cursor: 'pointer',
                padding: 0,
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
              }}
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
            <div style={{ marginBottom: '24px' }}>
              <div
                style={{
                  width: '100%',
                  height: '2px',
                  background: 'linear-gradient(to right, rgba(165, 8, 17, 1), rgba(165, 8, 17, 0))',
                  marginBottom: '20px',
                }}
              />

              <p
                style={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '13px',
                  marginBottom: '20px',
                  lineHeight: '1.5',
                }}
              >
                Select a date range (max 5 days) to ship logs to backend Elastic.
              </p>

              <div style={{ position: 'relative' }}>
                <label
                  style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '13px',
                    display: 'block',
                    marginBottom: '8px',
                  }}
                >
                  Date Range
                </label>
                <button
                  onClick={() => setShowPicker(!showPicker)}
                  style={{
                    width: '100%',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    color: fromDate && toDate ? 'white' : 'rgba(255, 255, 255, 0.5)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '6px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    outline: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  }}
                >
                  <span>{formatDateDisplay()}</span>
                  <Calendar size={18} color="rgba(255, 255, 255, 0.6)" />
                </button>

                {showPicker && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 8px)',
                      left: 0,
                      zIndex: 10,
                      width: '100%',
                    }}
                  >
                    <DateRangePicker
                      fromDate={fromDate}
                      toDate={toDate}
                      onDateChange={handleDateChange}
                      onClose={() => setShowPicker(false)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
            }}
          >
            <button
              onClick={onClose}
              style={{
                padding: '10px 20px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '6px',
                color: 'white',
                fontWeight: '500',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'all 0.2s ease',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleShipLogs}
              disabled={loading || !fromDate || !toDate}
              style={{
                padding: '10px 20px',
                backgroundColor: 'rgba(165, 8, 17, 1)',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                fontWeight: '500',
                cursor: loading || !fromDate || !toDate ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: loading || !fromDate || !toDate ? 0.6 : 1,
                transition: 'all 0.2s ease',
              }}
              onMouseOver={(e) => {
                if (!loading && fromDate && toDate) {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.backgroundColor = 'rgba(185, 10, 19, 1)';
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.backgroundColor = 'rgba(165, 8, 17, 1)';
              }}
            >
              {loading ? 'Submitting...' : 'Ship Logs'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
